import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExternalLinksService } from 'src/app/services/external-links.service';
import { SchedulesService, Schedule, Appointment, TimeSlot as ApiTimeSlot } from 'src/app/services/schedules.service';
import { CustomersService } from 'src/app/services/customers.service';
import { ExternalServiceProvidersService } from 'src/app/services/external-service-providers.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { Customer } from 'src/app/models/Customer';
import { ExternalServiceProvider } from 'src/app/models/ExternalServiceProvider';

interface TimeSlot {
  start: string;
  end: string;
}

interface CalendarDay {
  dayName: string;
  dayNumber: number;
  monthName: string;
  monthShort: string;
  fullDate: Date;
  available: boolean;
  timeSlots: TimeSlot[];
}

@Component({
  selector: 'app-appointments-external',
  templateUrl: './appointments-external.component.html',
  styleUrls: ['./appointments-external.component.css']
})
export class AppointmentsExternalComponent implements OnInit {

  isLoading = true;
  error: string | null = null;

  externalCode: string | null = null;
  clientId: number | null = null;
  providerId: number | null = null;
  scheduleId: number | null = null;

  schedule: Schedule | null = null;
  customer: Customer | null = null;
  provider: ExternalServiceProvider | null = null;

  // Dados do prestador (preenchidos automaticamente)
  providerName: string = '';
  providerCnpj: string = '';
  providerPhone: string = '';

  // Editáveis
  serviceDescription: string = '';
  serviceAddress: string = '';

  driver = {
    name: '',
    cpf: '',
    vehiclePlate: '',
    company: ''
  };

  // Calendar
  previewDays: CalendarDay[] = [];
  viewYear!: number;
  viewMonth!: number;
  selectedPreviewDay?: CalendarDay;
  selectedTimeSlot?: TimeSlot;

  private pendingLoads = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private externalLinksService: ExternalLinksService,
    private schedulesService: SchedulesService,
    private customersService: CustomersService,
    private externalServiceProvidersService: ExternalServiceProvidersService,
    private errorService: ErrorService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    this.viewYear = today.getFullYear();
    this.viewMonth = today.getMonth();

    this.externalCode = this.route.snapshot.paramMap.get('code');
    const clientIdParam = this.route.snapshot.queryParamMap.get('clientId');
    const providerIdParam = this.route.snapshot.queryParamMap.get('providerId');
    const scheduleIdParam = this.route.snapshot.queryParamMap.get('scheduleId');

    if (!this.externalCode || !clientIdParam) {
      this.error = 'Parâmetros inválidos para o agendamento externo.';
      this.isLoading = false;
      return;
    }

    this.clientId = +clientIdParam;
    this.providerId = providerIdParam ? +providerIdParam : null;
    this.scheduleId = scheduleIdParam ? +scheduleIdParam : null;

    if (!this.scheduleId) {
      this.error = 'Link de agendamento inválido (agenda não informada).';
      this.isLoading = false;
      return;
    }

    // Não validamos o código aqui — a validação é feita apenas no submit,
    // pois validateCode consome o código (uso único).
    this.loadAll(this.scheduleId);
  }

  private loadAll(scheduleId: number): void {
    this.pendingLoads = 2 + (this.providerId ? 1 : 0);
    this.loadSchedule(scheduleId);
    this.loadCustomer(this.clientId!);
    if (this.providerId) {
      this.loadProvider(this.providerId);
    }
  }

  private loadSchedule(scheduleId: number): void {
    this.schedulesService.getById(scheduleId).subscribe({
      next: (schedule) => {
        this.schedule = schedule;
        this.buildMonthlyPreview();
        this.completeLoad();
      },
      error: () => {
        this.error = 'Não foi possível carregar os dados da agenda.';
        this.isLoading = false;
      }
    });
  }

  private loadCustomer(customerId: number): void {
    this.customersService.getById(customerId).subscribe({
      next: (customer) => {
        this.customer = customer;
        this.driver.company = customer.name || '';
        this.completeLoad();
      },
      error: () => {
        this.error = 'Não foi possível carregar os dados do cliente.';
        this.isLoading = false;
      }
    });
  }

  private loadProvider(providerId: number): void {
    this.externalServiceProvidersService.getById(providerId).subscribe({
      next: (provider) => {
        this.provider = provider;
        this.providerName = provider.tradeName || provider.companyName || '';
        this.providerCnpj = provider.cnpj || '';
        this.providerPhone = provider.phone || '';
        this.serviceAddress = provider.fullAddress || '';
        this.serviceDescription = provider.serviceType || '';
        this.completeLoad();
      },
      error: () => {
        this.completeLoad();
      }
    });
  }

  private completeLoad(): void {
    this.pendingLoads--;
    if (this.pendingLoads <= 0) {
      this.isLoading = false;
    }
  }

  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear -= 1;
    } else {
      this.viewMonth -= 1;
    }
    this.buildMonthlyPreview();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear += 1;
    } else {
      this.viewMonth += 1;
    }
    this.buildMonthlyPreview();
  }

  getMonthName(monthIndex: number): string {
    const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    return months[monthIndex] || '';
  }

  private buildMonthlyPreview(): void {
    this.previewDays = [];
    const lastDayOfMonth = new Date(this.viewYear, this.viewMonth + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekdayMap = ['DOM','SEG','TER','QUA','QUI','SEX','SÁB'];

    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const date = new Date(this.viewYear, this.viewMonth, i);
      const isPast = date < today;
      const available = !isPast && this.isWeekdayAvailable(date.getDay());

      this.previewDays.push({
        dayName: weekdayMap[date.getDay()],
        dayNumber: i,
        monthName: this.getMonthName(this.viewMonth),
        monthShort: this.getMonthName(this.viewMonth).substring(0, 3),
        fullDate: date,
        available,
        timeSlots: available ? this.generateTimeSlots() : []
      });
    }
  }

  private isWeekdayAvailable(jsDay: number): boolean {
    if (!this.schedule) return jsDay !== 0 && jsDay !== 6;
    const s = this.schedule;
    const map = [s.Sunday, s.Monday, s.Tuesday, s.Wednesday, s.Thursday, s.Friday, s.Saturday];
    return !!map[jsDay];
  }

  private generateTimeSlots(): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const intervalMinutes = this.schedule?.TimeIntervalMinutes ?? 60;
    let start = new Date();
    start.setHours(8, 0, 0, 0);
    const endOfDay = new Date();
    endOfDay.setHours(18, 0, 0, 0);

    while (start < endOfDay) {
      const end = new Date(start.getTime() + intervalMinutes * 60000);
      slots.push({
        start: this.formatTime(start),
        end: this.formatTime(end)
      });
      start = end;
    }
    return slots;
  }

  private formatTime(date: Date): string {
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  selectPreviewDay(day: CalendarDay): void {
    if (!day.available) return;
    this.selectedPreviewDay = day;
    this.selectedTimeSlot = undefined;
  }

  chooseTimeSlot(slot: TimeSlot): void {
    this.selectedTimeSlot = slot;
  }

  submitAppointment(): void {
    if (!this.driver.name || !this.driver.cpf || !this.driver.vehiclePlate
      || !this.serviceDescription || !this.serviceAddress
      || !this.selectedPreviewDay || !this.selectedTimeSlot) {
      this.errorService.showErrorMessage('Por favor, preencha todos os campos obrigatórios e selecione um dia e horário.');
      return;
    }

    if (!this.schedule?.Id || !this.clientId) {
      this.errorService.showErrorMessage('Dados da agenda ou do cliente indisponíveis.');
      return;
    }

    if (!this.externalCode) {
      this.errorService.showErrorMessage('Código de agendamento externo inválido.');
      return;
    }

    // Cria o agendamento; só validamos (consumimos) o código após o sucesso.
    this.createAppointmentAfterValidation();
  }

  private createAppointmentAfterValidation(): void {
    const day = this.selectedPreviewDay!.fullDate;
    const yyyy = day.getFullYear();
    const mm = (day.getMonth() + 1).toString().padStart(2, '0');
    const dd = day.getDate().toString().padStart(2, '0');
    const dateOnly = `${yyyy}-${mm}-${dd}`;
    const dateStr = `${dateOnly}T00:00:00`;
    const startTime = `${this.selectedTimeSlot!.start}:00`;
    const endTime = `${this.selectedTimeSlot!.end}:00`;

    // Busca os time slots existentes para a agenda nesse dia.
    // Se já existir um para o horário escolhido, usamos seu Id.
    // Caso contrário, criamos um novo TimeSlot e depois o appointment.
    this.schedulesService.getAllTimeSlots(this.schedule!.Id!, dateOnly).subscribe({
      next: (existingSlots) => {
        const matched = (existingSlots || []).find(s =>
          this.normalizeTime(s.StartTime) === this.normalizeTime(startTime) &&
          this.normalizeTime(s.EndTime) === this.normalizeTime(endTime)
        );

        if (matched && matched.Id) {
          this.persistAppointment(dateStr, startTime, endTime, matched.Id);
        } else {
          this.createTimeSlotThenAppointment(dateStr, dateOnly, startTime, endTime);
        }
      },
      error: () => {
        this.errorService.showErrorMessage('Não foi possível verificar a disponibilidade do horário.');
      }
    });
  }

  private createTimeSlotThenAppointment(dateStr: string, dateOnly: string, startTime: string, endTime: string): void {
    const capacity = this.schedule?.AppointmentsByHours ?? 1;
    const newTimeSlot: ApiTimeSlot = {
      ScheduleId: this.schedule!.Id!,
      Date: dateStr,
      StartTime: startTime,
      EndTime: endTime,
      Capacity: capacity,
      BookedCount: 0
    };

    this.schedulesService.createTimeSlot(newTimeSlot).subscribe({
      next: (createdSlot) => {
        if (!createdSlot || !createdSlot.Id) {
          this.errorService.showErrorMessage('Erro ao criar horário. Tente novamente.');
          return;
        }
        this.persistAppointment(dateStr, startTime, endTime, createdSlot.Id);
      },
      error: () => {
        this.errorService.showErrorMessage('Erro ao criar horário. Tente novamente.');
      }
    });
  }

  private normalizeTime(value: string): string {
    if (!value) return '';
    const cleaned = value.replace(/^.*T/, '').split('.')[0];
    const parts = cleaned.split(':');
    const h = (parts[0] || '00').padStart(2, '0');
    const m = (parts[1] || '00').padStart(2, '0');
    const s = (parts[2] || '00').padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  private persistAppointment(dateStr: string, startTime: string, endTime: string, scheduleTimeSlotId: number): void {
    const subjectParts = [
      `Prestador: ${this.providerName || '-'}`,
      `Condutor: ${this.driver.name}`,
      `CPF: ${this.driver.cpf}`,
      `Placa: ${this.driver.vehiclePlate}`,
      `Empresa: ${this.driver.company || '-'}`,
      `Serviço: ${this.serviceDescription}`,
      `Endereço: ${this.serviceAddress}`
    ];
    const subject = subjectParts.join(' | ');

    const payload: Appointment = {
      ScheduleId: this.schedule!.Id!,
      ScheduleTimeSlotId: scheduleTimeSlotId,
      Date: dateStr,
      StartTime: startTime,
      EndTime: endTime,
      Subject: subject,
      Description: this.serviceDescription,
      CustomerId: this.clientId!,
      StaffId: 0,
      Status: 'Agendado',
      IsDeleted: false
    };

    this.schedulesService.createAppointment(payload).subscribe({
      next: () => {
        this.updateTimeSlotBookedCount(scheduleTimeSlotId);
        // Após criar o agendamento, consome (valida) o código externo
        // para garantir que o link não seja reutilizado.
        if (this.externalCode) {
          this.externalLinksService.validateCode(this.externalCode).subscribe({
            next: () => {
              this.errorService.showSuccessMessage('Agendamento realizado com sucesso!');
              this.resetForm();
            },
            error: () => {
              this.errorService.showSuccessMessage('Agendamento realizado com sucesso!');
              this.resetForm();
            }
          });
        } else {
          this.errorService.showSuccessMessage('Agendamento realizado com sucesso!');
          this.resetForm();
        }
      },
      error: () => {
        this.errorService.showErrorMessage('Não foi possível criar o agendamento. Tente novamente.');
      }
    });
  }

  private updateTimeSlotBookedCount(timeSlotId: number): void {
    this.schedulesService.getTimeSlotById(timeSlotId).subscribe({
      next: (timeSlot) => {
        const updated = { ...timeSlot, BookedCount: (timeSlot.BookedCount ?? 0) + 1 };
        this.schedulesService.updateTimeSlot(timeSlotId, updated).subscribe({
          next: () => {},
          error: () => {}
        });
      },
      error: () => {}
    });
  }

  private resetForm(): void {
    this.driver = { name: '', cpf: '', vehiclePlate: '', company: this.customer?.name || '' };
    this.selectedPreviewDay = undefined;
    this.selectedTimeSlot = undefined;
  }
}
